/**
 * Drei-Punkte-Menü mit Aktionen zu einer Zeile oder einem Objekt – überall dasselbe: jede
 * Aktion mit Icon links, Beschriftung in `body2`, zerstörende Aktionen erst beim Hover rot.
 *
 * Fachneutral: Die Aufrufer geben fertige Einträge mit Icon, Beschriftung und Handler.
 * Bestätigungen (etwa vor dem Löschen) öffnen sie selbst im Handler – das Menü schließt sich
 * vorher. Klicks reichen nicht bis zur Tabellenzeile durch, damit das Menü nicht nebenbei das
 * Detail öffnet.
 *
 * Drei Bausteine:
 * - `ActionsMenu` – Knopf und Menü in einem; der Normalfall.
 * - `ActionsMenuButton` – nur der Knopf, wenn der Aufrufer das Menü selbst führt (etwa weil es
 *   sich auch von anderer Stelle öffnet, wie das Menü am Nachweis vom Status-Chip aus).
 * - `ActionsMenuList` – nur das Menü an einem fremden Anker, z. B. am runden Knopf der Karte.
 *
 * Icons: dieselbe Aktion trägt überall dasselbe Icon. Jeder Statuswechsel trägt `ArrowForward`,
 * damit ein Menü voller Übergänge ruhig bleibt.
 */
import React, { useState } from "react";
import {
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PopoverOrigin,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { colors, fonts } from "../../../styles/theme";

export interface ActionsMenuItem {
  label: string;
  /** Icon links neben der Beschriftung, als `fontSize="small"` */
  icon: React.ReactElement;
  onClick: () => void;
  /** Zweite Zeile unter der Beschriftung, z. B. „Nächster Schritt“ */
  secondary?: string;
  /** Beschriftung halbfett – hebt den naheliegenden Eintrag hervor */
  emphasized?: boolean;
  /** Läuft gerade – Spinner statt Icon, Eintrag gesperrt */
  loading?: boolean;
  /** Trennstrich vor diesem Eintrag */
  dividerBefore?: boolean;
  /** Zerstörende Aktion (Löschen) – beim Hover in Fehlerfarbe */
  danger?: boolean;
  disabled?: boolean;
}

interface ActionsMenuListProps {
  items: ActionsMenuItem[];
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Kante, an der das Menü am Knopf ausgerichtet ist */
  align?: "left" | "right";
}

/** Nur das Menü – geöffnet, solange `anchor` gesetzt ist */
export const ActionsMenuList: React.FC<ActionsMenuListProps> = ({
  items,
  anchor,
  onClose,
  align = "right",
}) => {
  const anchorOrigin: PopoverOrigin = { vertical: "bottom", horizontal: align };
  const transformOrigin: PopoverOrigin = { vertical: "top", horizontal: align };

  return (
    <Menu
      open={!!anchor}
      anchorEl={anchor}
      onClose={onClose}
      onClick={(event) => event.stopPropagation()}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      {items.flatMap((item) => [
        ...(item.dividerBefore
          ? [<Divider key={`${item.label}-divider`} />]
          : []),
        <MenuItem
          key={item.label}
          disabled={item.disabled || item.loading}
          onClick={() => {
            onClose();
            item.onClick();
          }}
          // Zerstörend: neutral, erst beim Hover rot (components/CLAUDE.md)
          sx={
            item.danger
              ? {
                  "&:hover, &:hover .MuiListItemIcon-root": {
                    color: colors.status.error.main,
                  },
                }
              : undefined
          }
        >
          <ListItemIcon>
            {item.loading ? <CircularProgress size={16} /> : item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            secondary={item.secondary}
            slotProps={{
              primary: {
                variant: "body2",
                sx: {
                  fontWeight: item.emphasized
                    ? fonts.weights.semiBold
                    : undefined,
                },
              },
            }}
          />
        </MenuItem>,
      ])}
    </Menu>
  );
};

interface ActionsMenuButtonProps {
  onOpen: (anchor: HTMLElement) => void;
  /** Zugänglicher Name des Knopfs, z. B. „Aktionen zu Antrag Uppen" */
  ariaLabel?: string;
  tooltip?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

/** Nur der Knopf mit den drei Punkten */
export const ActionsMenuButton: React.FC<ActionsMenuButtonProps> = ({
  onOpen,
  ariaLabel = "Weitere Aktionen",
  tooltip = "Weitere Aktionen",
  size = "small",
  disabled,
}) => (
  <Tooltip title={tooltip} arrow>
    {/* span: Der Tooltip braucht ein Element, das auch gesperrt Mausereignisse bekommt */}
    <span>
      <IconButton
        size={size}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onOpen(event.currentTarget);
        }}
      >
        <MoreVertIcon fontSize={size} />
      </IconButton>
    </span>
  </Tooltip>
);

interface ActionsMenuProps
  extends
    Omit<ActionsMenuButtonProps, "onOpen">,
    Pick<ActionsMenuListProps, "align"> {
  items: ActionsMenuItem[];
}

/** Knopf und Menü in einem; ohne Einträge erscheint nichts */
const ActionsMenu: React.FC<ActionsMenuProps> = ({
  items,
  align,
  ...buttonProps
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (items.length === 0) return null;

  return (
    <>
      <ActionsMenuButton {...buttonProps} onOpen={setAnchor} />
      <ActionsMenuList
        items={items}
        anchor={anchor}
        onClose={() => setAnchor(null)}
        align={align}
      />
    </>
  );
};

export default ActionsMenu;
